import { InputFlag, mayRun, spentAfter } from "odyssey-shared";
import { PAGE_RULES } from "../rules.ts";
import { localStore, readRunMode, writeRunMode, type SettingsStore } from "../settings.ts";
import { TOGGLE_OFF, runIntent, toggleAfter, type RunMode, type ToggleEvent, type ToggleState } from "./intent.ts";

/**
 * The page's run (ADR-116 decisions 8, 11 and 12, P2.20-T6): the Run setting kept per device, the toggle's switch,
 * and what the map server last told the page of its character's Stamina, from which the page says whether the player
 * asks to run and predicts whether the map server will let the step run. The page only asks, with the sprint bit; the
 * prediction is the shared rule (`mayRun`) fed the page's own, slightly stale, state, and a step it gets wrong is
 * corrected by the next acknowledgement like any other.
 */

/**
 * What the sender of inputs needs of the page's run, read once for each input.
 */
export interface RunAsk {
  /**
   * Whether the player asks to run now: Shift held in Hold, the switch on in Toggle.
   *
   * @returns True when an input with a direction should carry the sprint bit.
   */
  asks(): boolean;
  /**
   * Whether the page expects the map server to let a step run, from the state it was last told.
   *
   * @param limits The `EffectLimit` bits the server confirmed on the character.
   * @param dead Whether the character is dead.
   * @returns True when the predictor should step the input at the run's length.
   */
  allowed(limits: number, dead: boolean): boolean;
}

const KEY_DOWN: { kind: "down"; shift: boolean; owned: boolean; repeat: boolean } = { kind: "down", shift: false, owned: false, repeat: false };
const KEY_UP: { kind: "up"; shift: boolean; owned: boolean } = { kind: "up", shift: false, owned: false };
const LOST: ToggleEvent = Object.freeze({ kind: "lost" });
const PRESS: ToggleEvent = Object.freeze({ kind: "press" });
const OFF: ToggleEvent = Object.freeze({ kind: "off" });

/**
 * The Run setting, the toggle's switch and the character's Stamina as the page knows it.
 *
 * @example
 * const run = new PageRun(localStore(), PAGE_RULES.constants.run_restart);
 * run.asks(shiftDown);
 */
export class PageRun {
  private modeNow: RunMode;
  private toggle: ToggleState = TOGGLE_OFF;
  private touch = false;
  private stamina = 0;
  private spent = false;
  private graced = false;

  /**
   * Reads the mode the device kept.
   *
   * @param store Where the mode is kept, or null when there is no store.
   * @param restart The rules' `run_restart`, the Stamina a spent character must hold before it runs again.
   */
  public constructor(
    private readonly store: SettingsStore | null,
    private readonly restart: number,
  ) {
    this.modeNow = readRunMode(store);
  }

  /**
   * Whether the touch Sprint button's latch is on: a switch of its own, a toggle whatever the Run setting says (ADR-061's
   * amendment of 2026-10-31, decision 7), turned off wherever the keys' switch is.
   *
   * @returns True while the button holds running on.
   */
  public get touched(): boolean {
    return this.touch;
  }

  /**
   * Flips the touch Sprint button's latch.
   *
   * @returns The latch after the flip.
   */
  public touchToggle(): boolean {
    this.touch = !this.touch;
    return this.touch;
  }

  /**
   * The Run setting in force.
   *
   * @returns Hold or Toggle.
   */
  public get mode(): RunMode {
    return this.modeNow;
  }

  /**
   * Whether the toggle's switch is on.
   *
   * @returns True while running is toggled on.
   */
  public get toggled(): boolean {
    return this.toggle.on;
  }

  /**
   * Puts a mode in force and keeps it. A change turns the switch off, so choosing Toggle never starts a run and
   * choosing Hold leaves none behind.
   *
   * @param mode Hold or Toggle.
   */
  public setMode(mode: RunMode): void {
    if (mode === this.modeNow) {
      return;
    }
    this.modeNow = mode;
    this.toggle = TOGGLE_OFF;
    writeRunMode(mode, this.store);
  }

  /**
   * Hears a key going down or up, as the input router shows every key (`GameInput.watchKeys`): the toggle's switch
   * follows it (`toggleAfter`). Nothing is made for it.
   *
   * @param run Whether the key is Sprint's, by the key table (P1.9-T174): its own key or one that answers for it.
   * @param down True for a press, false for a release.
   * @param owned Whether the game owned the keyboard then.
   * @param repeat Whether a press is a held key's auto-repeat.
   */
  public key(run: boolean, down: boolean, owned: boolean, repeat: boolean): void {
    if (down) {
      KEY_DOWN.shift = run;
      KEY_DOWN.owned = owned;
      KEY_DOWN.repeat = repeat;
      this.toggle = toggleAfter(this.toggle, KEY_DOWN);
    } else {
      KEY_UP.shift = run;
      KEY_UP.owned = owned;
      this.toggle = toggleAfter(this.toggle, KEY_UP);
    }
  }

  /**
   * A pointer was pressed anywhere on the page: a Shift held through it no longer flips the switch at its release, as
   * another key pressed with it would not. Running toggled on stays on.
   */
  public pressed(): void {
    this.toggle = toggleAfter(this.toggle, PRESS);
  }

  /**
   * The keyboard or the window left the game: a Shift still down no longer flips the switch at its release. Running
   * toggled on stays on.
   */
  public lost(): void {
    this.toggle = toggleAfter(this.toggle, LOST);
  }

  /**
   * Turns the switch off, as the world loading again does (ADR-116 decision 11), and forgets what the last session
   * told of the character.
   */
  public reset(): void {
    this.toggle = toggleAfter(this.toggle, OFF);
    this.touch = false;
    this.stamina = 0;
    this.spent = false;
    this.graced = false;
  }

  /**
   * Takes the character's `vitals`: the Stamina it holds, from which the page follows whether it is spent as the map
   * server does (`spentAfter`), and whether it is dead. A spent or dead character's switch is turned off, so a run
   * never resumes by itself.
   *
   * @param stamina The Stamina the character holds, in whole points.
   * @param dead Whether it is dead.
   */
  public vitals(stamina: number, dead: boolean): void {
    this.stamina = stamina;
    this.spent = spentAfter(this.spent, stamina, this.restart);
    if (this.spent || dead) {
      this.toggle = toggleAfter(this.toggle, OFF);
      this.touch = false;
    }
  }

  /**
   * Takes whether the character is held by a freeze, as its own snapshot's status byte says through the hold and the
   * grace after it (ADR-056's amendment of 2026-10-06): a held character does not move and one in the grace walks, so
   * the page predicts no run step for either. A held character's switch is turned off, as a spent or dead one's is,
   * so a toggled run does not start again by itself when the grace ends. Called each frame; nothing is made for it.
   *
   * @param held Whether the character shows as held.
   */
  public hold(held: boolean): void {
    this.graced = held;
    if (held) {
      this.toggle = toggleAfter(this.toggle, OFF);
      this.touch = false;
    }
  }

  /**
   * Whether the player asks to run now.
   *
   * @param shiftDown Whether Shift is held, as the game's own keyboard reads it.
   * @returns True when an input with a direction should carry the sprint bit: Shift held in Hold, the switch on in
   *   Toggle, or the touch button's latch.
   */
  public asks(shiftDown: boolean): boolean {
    return this.touch || runIntent(this.modeNow, shiftDown, this.toggle.on);
  }

  /**
   * Whether the page expects the map server to let a step run (`mayRun`, the map server's own rule), from the Stamina
   * and the held state it was last told.
   *
   * @param limits The `EffectLimit` bits the server confirmed on the character.
   * @param dead Whether the character is dead.
   * @returns True when the predictor should step the input at the run's length.
   */
  public allowed(limits: number, dead: boolean): boolean {
    return mayRun(InputFlag.Sprint, limits, this.stamina, this.spent, !dead, this.graced);
  }
}

/**
 * This page's run, its mode read from the device's store as the page loads.
 */
export const pageRun = new PageRun(localStore(), PAGE_RULES.constants.run_restart);
